import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Lottie from 'lottie-react';
import bgImage from '../images/background.png';
import mugBg from '../images/user-background.png';
import waveDarkAnim from '../animations/wave-dark.json';
import waveLightAnim from '../animations/wave-light.json';
import winePourSfx from '../sounds/wine-pour.wav';
import homeMusicSrc from '../sounds/home.mp3';
import { playTrack, stopTrack, setMuted } from '../functions/musicManager';
import { PROFILE_IMAGES } from '../constants/profileImages';
import { prefetchUrl } from '../functions/swipeCache';

function HmIcon({ name, size = 22 }) {
    const props = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
    switch (name) {
        case 'gang':
            return <svg {...props}><path d="M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" /><path d="M16 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" /><path d="M2 19c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M14 13c1-.6 2.2-1 3.5-1 2.5 0 4.5 2 4.5 4.5" /></svg>;
        case 'ranking':
            return <svg {...props}><path d="M6 9V4h12v5a6 6 0 0 1-12 0Z" /><path d="M6 4H3v3a3 3 0 0 0 3 3" /><path d="M18 4h3v3a3 3 0 0 1-3 3" /><path d="M9 21h6" /><path d="M12 15v6" /></svg>;
        case 'stats':
            return <svg {...props}><path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-5" /></svg>;
        case 'sun':
            return <svg {...props}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></svg>;
        case 'moon':
            return <svg {...props}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
        case 'sound-on':
            return <svg {...props}><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>;
        case 'sound-off':
            return <svg {...props}><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>;
        default: return null;
    }
}

export default function Home() {
    const { t, i18n } = useTranslation();
    const [profileInfo, setProfileInfo] = useState({ username: '', average: 0.00, wines: 0, profile: 0, id: 0 });
    const [notifications, setNotifications] = useState({ invitations: [], winegangs: [] });
    const [hasGang, setHasGang] = useState(false);
    const [profileReload, setProfileReload] = useState(false);
    const [theme, setTheme] = useState(() => document.documentElement.getAttribute('data-theme') || 'dark');
    const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('sound') !== 'off');
    const [ready, setReady] = useState(false);
    const [exiting, setExiting] = useState(false);
    const [waveActive, setWaveActive] = useState(false);
    const lottieRef = useRef(null);
    const sfxRef = useRef(null);
    const navigate = useNavigate();

    const toggleTheme = () => {
        const next = theme === 'dark' ? 'light' : 'dark';
        document.documentElement.setAttribute('data-theme', next);
        localStorage.setItem('theme', next);
        setTheme(next);
    };

    const toggleSound = () => {
        const next = !soundEnabled;
        setSoundEnabled(next);
        localStorage.setItem('sound', next ? 'on' : 'off');
        if (next) {
            // Must call playTrack here (user gesture) so iOS allows audio.play()
            playTrack('home', homeMusicSrc, 0.4);
        } else {
            setMuted(true);
        }
    };

    const toggleLang = () => {
        const newLang = i18n.language === 'es' ? 'en' : 'es';
        i18n.changeLanguage(newLang);
        localStorage.setItem('lang', newLang);
    };

    useEffect(() => {
        requestAnimationFrame(() => setReady(true));
        playTrack('home', homeMusicSrc, 0.4);
    }, []);

    useEffect(() => {
        const storedUserInfo = localStorage.getItem('userLogged');
        if (storedUserInfo) {
            const userInfo = JSON.parse(storedUserInfo);
            setProfileInfo({
                username: userInfo.username,
                average: userInfo.average || 0.00,
                wines: userInfo.wines || 0,
                id: userInfo.id || 0,
                profile: userInfo.profile || 0,
            });
            fetchUserData(userInfo);
        }
    }, []);

    const fetchUserData = (userInfo) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/userlog/${userInfo.id}`, true);
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4 && xhr.status === 200) {
                const data = JSON.parse(xhr.responseText);
                const updated = { ...userInfo, average: data.average, wines: data.wines, username: data.username };
                localStorage.setItem('userLogged', JSON.stringify(updated));
                setProfileInfo(updated);
                setProfileReload(prev => !prev);
                if (data.in_winegang) setHasGang(true);
            }
        };
        xhr.send();
    };

    useEffect(() => {
        if (!profileInfo.id) return;
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/hasNotifications/${profileInfo.id}`, true);
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4 && xhr.status === 200) {
                const data = JSON.parse(xhr.responseText);
                setNotifications({ invitations: data.invitations || [], winegangs: data.winegangs || [] });
            }
        };
        xhr.send();
    }, [profileInfo.id]);

    const handlePlay = (e) => {
        e.preventDefault();
        if (exiting) return;
        setExiting(true);
        setWaveActive(true);

        if (soundEnabled) {
            const sfx = new Audio(winePourSfx);
            sfx.volume = 0.8;
            sfx.currentTime = 1;
            sfx.play().catch(() => {});
            sfxRef.current = sfx;
        }

        stopTrack('home');
    };

    // Cuando la ola termina de subir → navegar
    const handleWaveComplete = () => {
        if (sfxRef.current) {
            sfxRef.current.pause();
            sfxRef.current.currentTime = 0;
        }
        sessionStorage.setItem('waveIn', '1');
        navigate('/choose-wine');
    };

    const hasNotifications = notifications.invitations.length > 0 || notifications.winegangs.length > 0;
    const avatarSrc = PROFILE_IMAGES[profileInfo.profile] || PROFILE_IMAGES[0];
    const waveAnim = theme === 'dark' ? waveDarkAnim : waveLightAnim;

    // ── Swipe gestures (mobile/tablet only) ──────────────────────────
    const swipeTouchX   = useRef(null);
    const swipeTouchY   = useRef(null);
    const swipePrefired = useRef(false); // prefetch triggered once per gesture

    const handleSwipeStart = (e) => {
        swipeTouchX.current   = e.touches[0].clientX;
        swipeTouchY.current   = e.touches[0].clientY;
        swipePrefired.current = false;
    };

    const handleSwipeMove = (e) => {
        if (swipeTouchX.current === null || swipePrefired.current) return;
        if (window.innerWidth >= 1100) return;
        const dx = e.touches[0].clientX - swipeTouchX.current;
        const dy = e.touches[0].clientY - swipeTouchY.current;
        if (Math.abs(dx) < 30) return;              // not enough to know direction yet
        if (Math.abs(dy) > Math.abs(dx) * 0.8) return; // mostly vertical
        swipePrefired.current = true;
        const uid = profileInfo.id;
        if (dx > 0 && uid) {
            // Heading to /gangs — prefetch user gang data
            prefetchUrl(`${process.env.REACT_APP_API_URL}/user/${uid}`);
            prefetchUrl(`${process.env.REACT_APP_API_URL}/hasNotifications/${uid}`);
        } else if (dx < 0) {
            // Heading to /ranking — prefetch leaderboard
            prefetchUrl(`${process.env.REACT_APP_API_URL}/users`);
        }
    };

    const handleSwipeEnd = (e) => {
        if (swipeTouchX.current === null) return;
        const dx = e.changedTouches[0].clientX - swipeTouchX.current;
        const dy = e.changedTouches[0].clientY - swipeTouchY.current;
        swipeTouchX.current = null;
        swipeTouchY.current = null;
        if (window.innerWidth >= 1100) return;
        if (Math.abs(dy) > Math.abs(dx) * 0.8) return;
        if (Math.abs(dx) < 75) return;
        const dest = dx > 0 ? '/gangs' : '/ranking';
        sessionStorage.setItem('swipeDir', dx > 0 ? 'from-right' : 'from-left');
        navigate(dest);
    };

    return (
        <div
            className={`hm-stage${ready ? ' hm-ready' : ''}${exiting ? ' hm-exiting' : ''}`}
            onTouchStart={handleSwipeStart}
            onTouchMove={handleSwipeMove}
            onTouchEnd={handleSwipeEnd}
        >
            <div className="hm-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="hm-vignette" />
            <div className="hm-grain" />

            {/* Header unificado — avatar + (botones encima / pill info debajo) */}
            <div className="hm-header-block">
                <Link to={`/user/${profileInfo.id}`} className="hm-avatar-link">
                    <div className="hm-avatar-frame" style={{ backgroundImage: `url(${mugBg})` }}>
                        <img src={avatarSrc} alt="" className="hm-avatar" />
                    </div>
                </Link>
                <div className="hm-header-right">
                    <div className="hm-top-actions">
                        <button className="hm-icon-btn" onClick={toggleSound} aria-label={soundEnabled ? t('home.aria_sound_off') : t('home.aria_sound_on')}>
                            <HmIcon name={soundEnabled ? 'sound-on' : 'sound-off'} size={15} />
                            <span className="hm-icon-label">{t('home.label_music')}</span>
                        </button>
                        <button className="hm-icon-btn" onClick={() => navigate(`/stats/${profileInfo.id}`)} aria-label={t('common.stats')}>
                            <HmIcon name="stats" size={15} />
                            <span className="hm-icon-label">{t('home.label_stats')}</span>
                        </button>
                        <button className="hm-icon-btn" onClick={toggleTheme} aria-label={t('home.aria_theme')}>
                            <HmIcon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
                            <span className="hm-icon-label">{theme === 'dark' ? t('home.label_theme_light') : t('home.label_theme_dark')}</span>
                        </button>
                        <button className="hm-icon-btn hm-icon-btn--lang" onClick={toggleLang} aria-label={t('home.aria_language')}>
                            <span className="hm-lang-code">{i18n.language === 'es' ? 'EN' : 'ES'}</span>
                            <span className="hm-icon-label">{t('home.label_language')}</span>
                        </button>
                    </div>
                    <Link to={`/user/${profileInfo.id}`} className="hm-profile-info-link">
                        <div className="hm-profile-info">
                            <div className="hm-profile-name">{profileInfo.username}</div>
                            <div className="hm-profile-stats">
                                <span><strong>{parseFloat(profileInfo.average || 0).toFixed(1)}%</strong> {t('common.accuracy')}</span>
                                <span><strong>{profileInfo.wines}</strong> {t('common.wines')}</span>
                            </div>
                        </div>
                    </Link>
                </div>
            </div>

            <div className="hm-sheet">
                <button onClick={handlePlay} className="hm-play-btn">
                    <span className="hm-play-icon">
                        <svg width="16" height="18" viewBox="0 0 16 18">
                            <path d="M1 1.5v15a1 1 0 0 0 1.5.87l13-7.5a1 1 0 0 0 0-1.74l-13-7.5A1 1 0 0 0 1 1.5Z" fill="#e8dcc4" />
                        </svg>
                    </span>
                    <span className="hm-play-text">{t('home.play')}</span>
                </button>
                <div className="hm-secondary-row">
                    <Link to="/gangs" className="hm-sec-btn">
                        {hasGang && <span className="hm-gang-dot" />}
                        <div className="hm-sec-icon-wrap">
                            <HmIcon name="gang" size={18} />
                            {hasNotifications && <span className="hm-notify-dot" />}
                        </div>
                        <span>WineGang</span>
                    </Link>
                    <Link to="/ranking" className="hm-sec-btn">
                        <HmIcon name="ranking" size={18} />
                        <span>{t('common.ranking')}</span>
                    </Link>
                </div>
            </div>

            {/* Ola de transición — sube al pulsar Play */}
            {waveActive && (
                <div className="wave-transition-overlay">
                    <Lottie
                        animationData={waveAnim}
                        lottieRef={lottieRef}
                        autoplay={true}
                        loop={false}
                        speed={2.2}
                        onComplete={handleWaveComplete}
                        rendererSettings={{ preserveAspectRatio: 'xMidYMax slice' }}
                        style={{ width: '100%', height: '100%' }}
                    />
                </div>
            )}
        </div>
    );
}
